// Bluetooth Mesh security toolbox (Mesh Profile 3.8.2): AES-CMAC, AES-CCM,
// s1 / k1 / k2 / k3 / k4, P-256 ECDH.
//
// Ported from the cob-mesh-reactjs sampler (src/mesh/crypto.ts). The one change:
// the AES block cipher is the one the BLE lamp codecs already use
// (../lamp/crypto.js aes128EncryptBlock) instead of @noble/ciphers. CMAC and the
// CCM mode used by Mesh only ever ENCRYPT blocks, so that is all that is needed,
// and meshCrypto.test.js pins it to the RFC 4493 / RFC 3610 / Mesh spec vectors.
// P-256 is @noble/curves 1.9 — the 1.x line, because it ships CommonJS that Jest
// loads without a transform (2.x is ESM only).

import { p256 } from '@noble/curves/p256';
import { aes128EncryptBlock } from '../lamp/crypto';
import { ascii, concat, equal, xor } from './bytes';

/* eslint-disable no-bitwise */

export const aesEcb = (key, block) => aes128EncryptBlock(key, block);

// ---------- AES-CMAC (RFC 4493) ----------
const dbl = (b) => {
  const out = new Uint8Array(16);
  for (let i = 0; i < 16; i += 1) out[i] = ((b[i] << 1) | (i < 15 ? b[i + 1] >> 7 : 0)) & 0xff;
  if (b[0] & 0x80) out[15] ^= 0x87;
  return out;
};

export const aesCmac = (key, msg) => {
  const L = aesEcb(key, new Uint8Array(16));
  const K1 = dbl(L);
  const K2 = dbl(K1);
  const n = Math.max(1, Math.ceil(msg.length / 16));
  const complete = msg.length > 0 && msg.length % 16 === 0;
  let last;
  if (complete) {
    last = xor(msg.slice((n - 1) * 16, n * 16), K1);
  } else {
    const pad = new Uint8Array(16);
    const rem = msg.slice((n - 1) * 16);
    pad.set(rem);
    pad[rem.length] = 0x80;
    last = xor(pad, K2);
  }
  let x = new Uint8Array(16);
  for (let i = 0; i < n - 1; i += 1) x = aesEcb(key, xor(x, msg.slice(i * 16, i * 16 + 16)));
  return aesEcb(key, xor(x, last));
};

// ---------- AES-CCM (RFC 3610, L=2, 13-byte nonce) ----------
function ccmCore(key, nonce, data, micLen, aad, encrypt, tagIn) {
  if (nonce.length !== 13) throw new Error('CCM nonce must be 13 bytes');
  const ctrBlock = (i) => concat(Uint8Array.of(0x01), nonce, Uint8Array.of(i >> 8, i & 0xff));
  const out = new Uint8Array(data.length);
  for (let i = 0; i < data.length; i += 16) {
    const s = aesEcb(key, ctrBlock(i / 16 + 1));
    for (let j = 0; j < 16 && i + j < data.length; j += 1) out[i + j] = data[i + j] ^ s[j];
  }
  const plain = encrypt ? data : out;
  const flags = (aad.length ? 0x40 : 0) | (((micLen - 2) / 2) << 3) | 0x01;
  let x = aesEcb(key, concat(Uint8Array.of(flags), nonce, Uint8Array.of(plain.length >> 8, plain.length & 0xff)));
  const macBlocks = (buf) => {
    for (let i = 0; i < buf.length; i += 16) {
      const blk = new Uint8Array(16);
      blk.set(buf.slice(i, i + 16));
      x = aesEcb(key, xor(x, blk));
    }
  };
  if (aad.length) macBlocks(concat(Uint8Array.of(aad.length >> 8, aad.length & 0xff), aad));
  macBlocks(plain);
  const s0 = aesEcb(key, ctrBlock(0));
  const tag = xor(x.slice(0, micLen), s0.slice(0, micLen));
  if (!encrypt && tagIn && !equal(tag, tagIn)) throw new Error('MIC check failed');
  return { out, tag };
}

/** -> ciphertext || MIC */
export const ccmEncrypt = (key, nonce, plain, micLen, aad = new Uint8Array(0)) => {
  const { out, tag } = ccmCore(key, nonce, plain, micLen, aad, true);
  return concat(out, tag);
};

/** ciphertext || MIC -> plaintext; throws if the MIC is wrong. */
export const ccmDecrypt = (key, nonce, cipherAndMic, micLen, aad = new Uint8Array(0)) => {
  const c = cipherAndMic.slice(0, cipherAndMic.length - micLen);
  const t = cipherAndMic.slice(cipherAndMic.length - micLen);
  return ccmCore(key, nonce, c, micLen, aad, false, t).out;
};

// ---------- Mesh key derivation ----------
const ZERO16 = new Uint8Array(16);
export const s1 = (m) => aesCmac(ZERO16, m);
export const k1 = (n, salt, p) => aesCmac(aesCmac(salt, n), p);

export const k2 = (n, p = Uint8Array.of(0)) => {
  const t = aesCmac(s1(ascii('smk2')), n);
  const t1 = aesCmac(t, concat(p, Uint8Array.of(1)));
  const t2 = aesCmac(t, concat(t1, p, Uint8Array.of(2)));
  const t3 = aesCmac(t, concat(t2, p, Uint8Array.of(3)));
  return { nid: t1[15] & 0x7f, encryptionKey: t2, privacyKey: t3 };
};

/** Network ID — what a proxy of THIS network advertises in its 0x1828 service data. */
export const k3 = (n) => aesCmac(aesCmac(s1(ascii('smk3')), n), concat(ascii('id64'), Uint8Array.of(1))).slice(8);

export const k4 = (n) => aesCmac(aesCmac(s1(ascii('smk4')), n), concat(ascii('id6'), Uint8Array.of(1)))[15] & 0x3f;

// ---------- P-256 ECDH ----------
export const ecdhKeyPair = () => {
  const secretKey = p256.utils.randomPrivateKey();
  const pub = p256.getPublicKey(secretKey, false); // 0x04 || X || Y
  return { secretKey, publicKey: pub.slice(1) }; // Mesh sends the 64-byte X||Y
};

/** -> 32-byte X coordinate of the shared point; throws on an invalid peer key. */
export const ecdhSecret = (secretKey, peerXY) => p256.getSharedSecret(secretKey, concat(Uint8Array.of(0x04), peerXY), true).slice(1);
